import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { formatBrlFromCents, formatCompetenciaLabel } from "./FinanceUi";

/** Padrão visual dos PDFs financeiros XVISION (mesmo chrome do relatório de investidores). */
const BRAND = [25, 118, 210];
const MUTED = [100, 116, 139];
const INK = [15, 23, 42];

function money(cents) {
  return formatBrlFromCents(cents);
}

function addFooter(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    doc.setDrawColor(226, 232, 240);
    doc.line(14, h - 12, w - 14, h - 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text(
      "Confidencial — XVISION · demonstrativo para conferência e anexo ao lançamento no ERP",
      14,
      h - 7
    );
    doc.text(`${i} / ${pageCount}`, w - 14, h - 7, { align: "right" });
  }
}

function sectionTitle(doc, title, y) {
  const pageH = doc.internal.pageSize.getHeight();
  let cursor = y;
  if (cursor > pageH - 40) {
    doc.addPage();
    cursor = 18;
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...INK);
  doc.text(title, 14, cursor);
  doc.setDrawColor(...BRAND);
  doc.setLineWidth(0.6);
  doc.line(14, cursor + 2, 70, cursor + 2);
  return cursor + 8;
}

function kvTable(doc, startY, rows) {
  doc.autoTable({
    startY,
    head: [["Campo", "Valor"]],
    body: rows,
    theme: "striped",
    styles: { fontSize: 8.5, cellPadding: 2.2, textColor: INK },
    headStyles: { fillColor: BRAND, textColor: 255, fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 70, fontStyle: "bold" },
      1: { cellWidth: "auto" },
    },
    margin: { left: 14, right: 14 },
  });
  return doc.lastAutoTable.finalY + 8;
}

function dataTable(doc, startY, head, body, opts = {}) {
  doc.autoTable({
    startY,
    head: [head],
    body,
    theme: "striped",
    styles: {
      fontSize: opts.fontSize || 8,
      cellPadding: 2,
      textColor: INK,
      overflow: "linebreak",
    },
    headStyles: { fillColor: BRAND, textColor: 255, fontStyle: "bold" },
    margin: { left: 14, right: 14 },
    ...opts,
  });
  return doc.lastAutoTable.finalY + 8;
}

function safeSlug(value) {
  return String(value || "pessoa")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48)
    .toLowerCase();
}

function saleKindLabel(kind) {
  if (kind === "upgrade") return "Upgrade";
  if (kind === "new") return "Novo";
  return kind || "—";
}

/**
 * Gera e baixa PDF de demonstrativo de comissão por pessoa (padrão XVISION).
 * @param {{ periodYm: string, person: object, fileName?: string }} args
 */
export function exportCommissionPersonReportPdf({ periodYm, person, fileName } = {}) {
  if (!periodYm) throw new Error("Competência (periodYm) é obrigatória.");
  if (!person?.person_id && !person?.id) {
    throw new Error("Pessoa é obrigatória para o demonstrativo.");
  }

  const items = Array.isArray(person.items) ? person.items : [];
  const payoutCents = Number(person.payout_cents ?? 0);
  const name = person.name || `Pessoa #${person.person_id || person.id}`;
  const competenciaLabel = formatCompetenciaLabel(periodYm) || periodYm;
  const generatedAt = new Date().toLocaleString("pt-BR");
  const pid = person.person_id || person.id;
  const safeName =
    fileName ||
    `xvision-comissao-${String(periodYm).replace(/[^\d-]/g, "")}-${safeSlug(name)}-${pid}`;

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  doc.setFillColor(...BRAND);
  doc.rect(0, 0, pageW, 42, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("XVISION", 14, 16);
  doc.setFontSize(14);
  doc.text("Demonstrativo de comissões", 14, 26);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Competência ${competenciaLabel} · time interno (parcelas creditadas)`, 14, 34);

  doc.setTextColor(...INK);
  doc.setFontSize(9);
  let y = 52;
  doc.text(`Gerado em: ${generatedAt}`, 14, y);
  y += 6;
  doc.setTextColor(...MUTED);
  doc.setFontSize(8);
  const disclaimer = doc.splitTextToSize(
    "Documento interno para conferência da pessoa e anexo ao lançamento no ERP. Valores referem-se às parcelas já creditadas (earned) na competência. Parcelas futuras podem ser perdidas em caso de desligamento ou cancelamento do cliente.",
    pageW - 28
  );
  doc.text(disclaimer, 14, y);
  y += disclaimer.length * 4 + 6;

  y = sectionTitle(doc, "1. Beneficiário", y);
  y = kvTable(doc, y, [
    ["Nome", name],
    ["E-mail", person.email || "—"],
    ["Contrato", person.contract_type ? String(person.contract_type).toUpperCase() : "—"],
    ["Faixa", person.band_key || "—"],
    ["ID pessoa", String(pid)],
    ["Competência", competenciaLabel],
  ]);

  y = sectionTitle(doc, "2. Resumo da competência", y);
  y = kvTable(doc, y, [
    ["Total a pagar", money(payoutCents)],
    ["Linhas / parcelas", String(items.length)],
  ]);

  y = sectionTitle(doc, "3. Extrato detalhado", y);
  if (!items.length) {
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text("Nenhuma parcela creditada nesta competência.", 14, y);
    y += 8;
  } else {
    y = dataTable(
      doc,
      y,
      ["Workspace", "Tipo", "Papel", "Parc.", "%", "Base", "Payout"],
      items.map((it) => [
        it.dominio_name || (it.dominio_id != null ? `#${it.dominio_id}` : "—"),
        saleKindLabel(it.sale_kind),
        it.role_label || it.role || "—",
        it.installment_index != null ? `${Number(it.installment_index)}/12` : "—",
        it.rate_pct != null ? `${(Number(it.rate_pct) * 100).toFixed(1)}%` : "—",
        money(it.base_cents ?? it.attributed_revenue_cents),
        money(it.payout_cents),
      ]),
      {
        columnStyles: {
          0: { cellWidth: 42 },
          1: { cellWidth: 18 },
          2: { cellWidth: 32 },
          3: { cellWidth: 16, halign: "center" },
          4: { cellWidth: 16, halign: "right" },
          5: { cellWidth: 28, halign: "right" },
          6: { cellWidth: 28, halign: "right" },
        },
      }
    );

    const leaderLines = items.filter(
      (it) => it.role === "leader_on_closer" || it.role === "leader_on_indicator"
    );
    if (leaderLines.some((it) => it.source_person_name)) {
      y = sectionTitle(doc, "4. Origem do spillover (gestor)", y);
      y = dataTable(
        doc,
        y,
        ["Workspace", "Sobre", "Payout"],
        leaderLines.map((it) => [
          it.dominio_name || "—",
          it.source_person_name || "—",
          money(it.payout_cents),
        ])
      );
    }
  }

  y = sectionTitle(doc, "Conferência", y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...INK);
  doc.text("Declaro ter conferido os valores acima para a competência informada.", 14, y);
  y += 14;
  doc.setDrawColor(...MUTED);
  doc.line(14, y, 90, y);
  doc.line(110, y, pageW - 14, y);
  doc.setTextColor(...MUTED);
  doc.setFontSize(7);
  doc.text("Assinatura / visto", 14, y + 4);
  doc.text("Data", 110, y + 4);

  addFooter(doc);
  doc.save(`${safeName}.pdf`);
  return { ok: true, fileName: `${safeName}.pdf` };
}

/**
 * Exporta um PDF por pessoa do relatório mensal (sequencial).
 */
export async function exportCommissionMonthReportPdfs({ periodYm, people } = {}) {
  const list = Array.isArray(people) ? people : [];
  if (!list.length) throw new Error("Nenhuma pessoa com comissão nesta competência.");
  const results = [];
  for (const person of list) {
    results.push(exportCommissionPersonReportPdf({ periodYm, person }));
  }
  return { ok: true, count: results.length, files: results.map((r) => r.fileName) };
}
